import React, { useState } from 'react'
import useReportsDefinition from './hooks/useReportsDefinition'
import { Tag, Classes } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { navigateWithBasepath as navigate, legacyMode } from 'App'
import Appbar from 'components/Appbar'
import ReportsSearch from './ReportsSearch'
import { Icon, PageContent } from '@ui'
import { IReport, IReportDefinition } from './types'
import track from '@track'
import client from '@stores/apollo'
import { navigateToAphrodite } from '@utils/navigateToAphrodite'
import cn from 'classnames'
import css from './reportsCatalog.module.scss'

let persistedState = 'All'

const fauxCategories = [
  'SOMETHING1',
  'SOMETHING2',
  'SOMETHING3',
  'SOMETHING4',
  'SOMETHING5',
  'SOMETHING6',
  'SOMETHING7',
  'SOMETHING8',
]

const fauxDefinition: IReportDefinition = {
  categories: ['SOMETHING1'],
  byCategory: {
    SOMETHING1: [
      {
        id: '1',
        name: 'SOMETHINGA',
        hidden: false,
        categoryName: 'SOMETHING1',
        newReport: false,
        description: 'A bunch of words that should not be seen by anyone',
      },
      {
        id: '2',
        name: 'SOMETHINGB',
        hidden: false,
        categoryName: 'SOMETHING1',
        newReport: false,
        description: 'A bunch of words that should not be seen by anyone',
      },
      {
        id: '3',
        name: 'SOMETHINGC',
        hidden: false,
        categoryName: 'SOMETHING1',
        newReport: false,
        description: 'A bunch of words that should not be seen by anyone',
      },
      {
        id: '4',
        name: 'SOMETHINGD',
        hidden: false,
        categoryName: 'SOMETHING1',
        newReport: false,
        description: 'A bunch of words that should not be seen by anyone',
      },
      {
        id: '5',
        name: 'SOMETHINGE',
        hidden: false,
        categoryName: 'SOMETHING1',
        newReport: false,
        description: 'A bunch of words that should not be seen by anyone',
      },
      {
        id: '6',
        name: 'SOMETHINGF',
        hidden: false,
        categoryName: 'SOMETHING1',
        newReport: false,
        description: 'A bunch of words that should not be seen by anyone',
      },
    ],
  },
  reports: [],
  availableReports: ['1', '2', '3', '4', '5', '6'],
}
export default function ReportsCatalog({ categories }: { categories: string }) {
  const { definition, loading } = useReportsDefinition({ client: client.gql })
  const [selected, setSelected] = useState(
    categories ? decodeURIComponent(categories) : persistedState
  )

  React.useEffect(() => {
    persistedState = selected
  }, [selected])

  React.useEffect(() => {
    setSelected(categories ? decodeURIComponent(categories) : persistedState)
  }, [categories])

  return (
    <>
      <Appbar title="Reports">
        <ReportsSearch
          reports={definition.reports.filter(
            (r) => !r.hidden && definition.availableReports.includes(r.id)
          )}
        />
      </Appbar>
      <PageContent>
        {loading || !definition.availableReports.length ? (
          <div className={css.page_wrapper}>
            <div className="sticky" style={{ top: 74, width: 260, flexShrink: 0 }}>
              {fauxCategories.map((category) => (
                <CategorySelector
                  count={0}
                  onClick={() => {}}
                  key={`cat-title-${category}`}
                  name={category}
                  isLoading={true}
                  isActive={false}
                />
              ))}
            </div>
            <div className={css.reports_wrapper}>
              <Category
                category="SOMETHING1"
                definition={fauxDefinition}
                key="cat-group-wrapper-faux"
                loading={true}
              />
            </div>
          </div>
        ) : (
          <div className={css.page_wrapper}>
            {legacyMode && (
              <div className="sticky" style={{ top: 74, width: 260, flexShrink: 0 }}>
                {definition.categories.map((category) => (
                  <CategorySelector
                    key={`cat-title-${category}`}
                    name={category}
                    isActive={category === selected}
                    count={definition.byCategory[category].length}
                    onClick={() => {
                      track.reportCatalog({
                        eventName: track.Event.TAB_CLICK,
                        props: {
                          tabName: category,
                        },
                      })
                      setSelected(category)
                    }}
                  />
                ))}
              </div>
            )}

            <div className={css.reports_wrapper}>
              {selected === 'All' ? (
                definition.categories.map((cat) => {
                  if (cat === 'All') {
                    return null
                  }
                  return (
                    <Category
                      category={cat}
                      definition={definition}
                      key={`cat-group-wrapper-${cat.split(' ').join('-')}`}
                    />
                  )
                })
              ) : (
                <Category
                  category={selected}
                  definition={definition}
                  key={`cat-group-wrapper-${selected.split(' ').join('-')}`}
                />
              )}
            </div>
          </div>
        )}
      </PageContent>
    </>
  )
}

function Category({
  category,
  definition,
  loading,
}: {
  category: string
  definition: IReportDefinition
  loading?: boolean
}) {
  return (
    <div className={css.group_wrapper}>
      <div className={css.title}>
        <span className={loading ? Classes.SKELETON : undefined}>{category}</span>
      </div>
      <div
        style={{
          border: '1px solid rgba(151, 151, 151, 0.16)',
          borderRadius: 4,
        }}>
        {definition.byCategory[category].map((report, i, reports) => {
          return (
            <Report
              loading={loading}
              key={`report-div-${report.id}`}
              report={report}
              last={i === reports.length - 1}
              enabled={definition.availableReports.includes(report.id)}
            />
          )
        })}
      </div>
    </div>
  )
}

function Report({
  report,
  last,
  enabled,
  loading,
}: {
  report: IReport
  last: boolean
  enabled: boolean
  loading?: boolean
}) {
  return (
    <>
      {enabled ? (
        <div
          className={css.report_wrapper}
          onClick={() => {
            navigate(`/reports/${encodeURIComponent(report.categoryName)}/${report.id}`, {})
          }}>
          <div className={css.main_report}>
            <div className={css.report_title}>
              <div
                className={cn('text-base font-semibold', {
                  [Classes.SKELETON]: loading,
                })}
                style={{
                  marginTop: 0,
                  marginBottom: 5,
                  marginRight: 10,
                }}>
                {report.name}
              </div>
              {report.newReport && <New />}
            </div>
            <div className={loading ? Classes.SKELETON : undefined} style={{ color: '#6A6869' }}>
              {report.description}
            </div>
          </div>
          <Icon name="chevron-right" />
        </div>
      ) : (
        <Tooltip2
          content="Report not available. Contact Customer Success to upgrade"
          targetProps={{ style: { width: '100%' } }}>
          <div className={css.reports_wrapper}>
            <div className={css.main_report}>
              <div className={css.report_title}>
                <h3
                  className={loading ? Classes.SKELETON : undefined}
                  style={{
                    marginTop: 0,
                    marginBottom: 5,
                    marginRight: 10,
                    color: '#9b9b9b',
                  }}>
                  {report.name}
                </h3>
                {report.newReport && <New />}
              </div>
              <div className={loading ? Classes.SKELETON : undefined} style={{ color: '#9b9b9b' }}>
                {report.description}
              </div>
            </div>
            <Icon name="chevron-right" style={{ color: '#a9a9a9' }} />
          </div>
        </Tooltip2>
      )}
      {!last && (
        <div
          style={{
            margin: '0 20px',
            borderBottom: '1px solid rgba(151, 151, 151, 0.16)',
          }}
        />
      )}
    </>
  )
}

function New() {
  return (
    <Tag minimal round style={{ height: 20 }} intent="primary">
      New
    </Tag>
  )
}

function CategorySelector({
  name,
  count,
  isActive,
  onClick,
  isLoading,
}: {
  name: string
  count: number
  isActive: boolean
  onClick: (id: string) => void
  isLoading?: boolean
}) {
  return (
    <div
      className={`
    flex
    justify-between
    items-center
    h-10
    text-base
    px-2
    rounded
    cursor-pointer
    ${isActive ? 'bg-primary-green' : 'hover:bg-grey-200'}
    ${isActive ? 'bg-primary-green' : 'bg-white'}
    ${isActive ? 'text-white' : 'text-grey-900'}
    ${isActive ? 'font-bold' : 'font-normal'}
    `}
      onClick={() => onClick(name)}>
      <div className={isLoading ? Classes.SKELETON : undefined}>{name}</div>
      <Tag
        className={isLoading ? Classes.SKELETON : undefined}
        minimal
        round
        style={{ color: isActive ? 'white' : 'black' }}>
        {count}
      </Tag>
    </div>
  )
}
